import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion,  AnimatePresence } from "framer-motion";
import {
  FaBuilding,
  FaFilter,
  
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import Api from "../../api";
import DataAtk from "../../components/atk/DataAtk";
import { SipDropdown, SipSearch } from "../../components/form/SipControls";

const AtkPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("semua");
  const [sortBy, setSortBy] = useState("nama");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Data states
  const [atks, setAtks] = useState([]);
  const [masuks, setMasuks] = useState([]);
  const [keluars, setKeluars] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pagination, setPagination] = useState({});
  const [categories, setCategories] = useState([]);

  // Debounce search
  const [searchDebounce, setSearchDebounce] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      // Add search parameter if exists
      if (searchDebounce.trim()) {
        params.append("search", searchDebounce.trim());
      }

      // Add category filter if not "semua"
      if (filterCategory !== "semua") {
        params.append("kategori", filterCategory);
      }

      const res = await Api.get(`/mon/atk?${params.toString()}`);

      setAtks(res.data.data || []);
      setMasuks(res.data.masuk || []);
      setKeluars(res.data.keluar || []);

      // Set pagination info
      if (res.data.pagination) {
        setTotalItems(res.data.pagination.total || 0);
        setTotalPages(res.data.pagination.last_page || 0);
        setPagination(res.data.pagination);
      }
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error("Error fetching ATK data:", error);
      setAtks([]);
      setMasuks([]);
      setKeluars([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, sortBy, sortOrder, searchDebounce, filterCategory]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchDebounce, filterCategory]);

  const handleBack = () => {
    navigate(-1);
  };

  // Fungsi untuk mendapatkan data masuk terakhir berdasarkan atk_id
  const getLatestMasuk = (atkId) => {
    const masukItems = masuks.filter((item) => item.atk_id === atkId);
    if (masukItems.length === 0) return 0;

    // Urutkan berdasarkan tanggal terbaru
    const sortedMasuk = masukItems.sort(
      (a, b) => new Date(b.tgl) - new Date(a.tgl)
    );
    return sortedMasuk[0].jml || 0;
  };

  // Fungsi untuk mendapatkan data keluar terakhir berdasarkan atk_id
  const getLatestKeluar = (atkId) => {
    const keluarItems = keluars.filter((item) => item.atk_id === atkId);
    if (keluarItems.length === 0) return 0;

    // Urutkan berdasarkan tanggal terbaru
    const sortedKeluar = keluarItems.sort(
      (a, b) => new Date(b.tgl) - new Date(a.tgl)
    );
    return sortedKeluar[0].vol || 0;
  };

  // Fungsi untuk menghitung total masuk berdasarkan atk_id
  const getTotalMasuk = (atkId) => {
    return masuks
      .filter((item) => item.atk_id === atkId)
      .reduce((total, item) => total + (item.jml || 0), 0);
  };

  // Fungsi untuk menghitung total keluar berdasarkan atk_id
  const getTotalKeluar = (atkId) => {
    return keluars
      .filter((item) => item.atk_id === atkId)
      .reduce((total, item) => total + (item.vol || 0), 0);
  };

  const calculateStokAkhir = (stokAwal, totalMasuk, totalKeluar) => {
    return stokAwal + totalMasuk - totalKeluar;
  };

  const getStokStatus = (stokAkhir) => {
    if (stokAkhir <= 10) return "text-red-600 bg-red-50";
    if (stokAkhir <= 20) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
  };

  return (
    <div className="space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* ==================== HEADER SECTION ==================== */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-primary px-3 py-4 md:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Judul Halaman */}
              <div className="text-white">
                <div className="flex items-center gap-3 mb-1">
                  <div className="p-1 bg-white/20 rounded-xl backdrop-blur-sm">
                    <FaBuilding className="text-xl" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold">
                    Alat Tulis Kantor
                  </h1>
                </div>
              </div>

              {/* Tombol Kembali */}
              <button
                onClick={handleBack}
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-6 py-3 rounded-xl hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span className="font-medium">Kembali</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* ==================== SUMMARY CARDS ==================== */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="bg-primary rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Total Item</p>
                <p className="text-2xl font-bold">
                  {loading ? "..." : totalItems}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <FaBuilding className="text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-accent rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Stok Aman</p>
                <p className="text-2xl font-bold">
                  {loading
                    ? "..."
                    : atks.filter((item) => {
                        const totalMasuk = getTotalMasuk(item.id);
                        const totalKeluar = getTotalKeluar(item.id);
                        const stok = calculateStokAkhir(
                          item.stokAwal || 0,
                          totalMasuk,
                          totalKeluar
                        );
                        return stok > 20;
                      }).length}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">✅</div>
            </div>
          </div>

          <div className="bg-warning rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm">Stok Menipis</p>
                <p className="text-2xl font-bold">
                  {loading
                    ? "..."
                    : atks.filter((item) => {
                        const totalMasuk = getTotalMasuk(item.id);
                        const totalKeluar = getTotalKeluar(item.id);
                        const stok = calculateStokAkhir(
                          item.stokAwal || 0,
                          totalMasuk,
                          totalKeluar
                        );
                        return stok > 10 && stok <= 20;
                      }).length}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">⚠️</div>
            </div>
          </div>

          <div className="bg-danger rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Stok Habis</p>
                <p className="text-2xl font-bold">
                  {loading
                    ? "..."
                    : atks.filter((item) => {
                        const totalMasuk = getTotalMasuk(item.id);
                        const totalKeluar = getTotalKeluar(item.id);
                        const stok = calculateStokAkhir(
                          item.stokAwal || 0,
                          totalMasuk,
                          totalKeluar
                        );
                        return stok <= 10;
                      }).length}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">🚨</div>
            </div>
          </div>
        </motion.div>

        {/* ==================== SEARCH AND FILTER SECTION ==================== */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            {/* Search Bar */}
            <div className="w-full min-w-0 flex-1 xl:max-w-md">
              <SipSearch
                placeholder="Cari berdasarkan nama, kode, atau spesifikasi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter dan Sort */}
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:w-auto xl:flex-wrap xl:items-center">
              {/* Filter Kategori */}
              <div className="flex min-w-0 items-center gap-2">
                <FaFilter className="shrink-0 text-gray-500" />
                <SipDropdown
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  options={[
                    { label: "Semua Kategori", value: "semua" },
                    ...categories.map((item) => ({ label: item, value: item })),
                  ]}
                />
              </div>

              {/* Sort By */}
              <SipDropdown
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="xl:w-44"
                options={[
                  { label: "Urutkan: Nama", value: "nama" },
                  { label: "Urutkan: Kode", value: "kode" },
                  { label: "Urutkan: Stok", value: "stokAwal" },
                ]}
              />

              {/* Sort Order */}
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 transition-all duration-300 hover:bg-gray-200 xl:w-auto"
              >
                {sortOrder === "asc" ? "↑" : "↓"}
                {sortOrder === "asc" ? "A-Z" : "Z-A"}
              </button>
            </div>
          </div>

          {/* Info hasil pencarian dan per page */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600">
              Menampilkan {pagination.from || 0} - {pagination.to || 0} dari{" "}
              {totalItems} item ATK
            </div>

            {/* Items per page */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Tampilkan:</span>
              <SipDropdown
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-24"
                filter={false}
                options={[5, 10, 25, 50, 100].map((count) => ({
                  label: String(count),
                  value: count,
                }))}
              />
              <span className="text-sm text-gray-600">item per halaman</span>
            </div>
          </div>
        </motion.div>

        {/* ==================== TABLE SECTION ==================== */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="overflow-x-auto">
            <DataAtk
              atks={atks}
              getLatestMasuk={getLatestMasuk}
              getLatestKeluar={getLatestKeluar}
              getTotalMasuk={getTotalMasuk}
              getTotalKeluar={getTotalKeluar}
              getStokStatus={getStokStatus}
              loading={loading}
              calculateStokAkhir={calculateStokAkhir}
              masuk={masuks}
              keluar={keluars}
            />

            {/* Empty State */}
            {!loading && atks.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg mb-2">📦</div>
                <div className="text-gray-500 font-medium">
                  Tidak ada data ATK ditemukan
                </div>
                <div className="text-gray-400 text-sm">
                  {searchTerm || filterCategory !== "semua"
                    ? "Coba ubah kata kunci pencarian atau filter"
                    : "Belum ada data ATK yang tersedia"}
                </div>
              </div>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {!loading && totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Pagination Info */}
                <div className="text-sm text-gray-700">
                  Halaman {currentPage} dari {totalPages}
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                    }`}
                  >
                    <FaChevronLeft className="text-xs" />
                    Sebelumnya
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let startPage = Math.max(
                        1,
                        currentPage - Math.floor(maxVisible / 2)
                      );
                      let endPage = Math.min(
                        totalPages,
                        startPage + maxVisible - 1
                      );

                      if (endPage - startPage + 1 < maxVisible) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      // First page
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setCurrentPage(1)}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span
                              key="ellipsis1"
                              className="px-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                      }

                      // Visible pages
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                              i === currentPage
                                ? "bg-primary text-white border border-primary"
                                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      // Last page
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span
                              key="ellipsis2"
                              className="px-2 text-gray-500"
                            >
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                    }`}
                  >
                    Selanjutnya
                    <FaChevronRight className="text-xs" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AtkPage;
